import { createAsyncThunk } from '@reduxjs/toolkit';
import { parsePhoneNumber } from 'react-phone-number-input';
import { CountryCode } from 'libphonenumber-js/types';

import { notify } from 'components/notification/notificationSlice';
import { getUserInviteData } from 'pages/login/utils';
import { getBusinessesId } from 'utils/authService';
import businessesService from 'services/businesses';
import countriesServices from 'services/countries';
import { addSmartDisburse } from 'services/erp';
import { searchCandidates } from 'services/dnb';
import personsServices from 'services/persons';
import { getPreRegistrationToken } from 'services/preRegistrationTokens';
import { getTenant } from 'theme/selectors';
import tandcService from 'services/tandc';
import { RootState } from 'state/store';
import {
  BusinessPersonProps,
  PersonalInfoProps,
  PersonProps,
  UserProps,
} from 'entities/profile';
import telephoneService, {
  PERSON_PHONE_TYPE_CODE,
  BUSINESS_PHONE_TYPE_CODE,
} from 'services/telephoneNumbers';

import {
  getBusiness,
  getBusinessesAddress,
  getPerson,
  getProfile,
} from './selectors';
import {
  setBusiness,
  setBusinessAddress,
  setBusinessAdmin,
  setPerson,
  setPersonTelephoneNumber,
} from './profileSlice';
import {
  searchOrCreatePerson,
  BusinessNotCreatedError,
  createBusinessProfile,
  updateBusinesses,
  updateBusinessProfile,
  getBusinessById,
} from './api';
import {
  getMappedCountries,
  getProfileData,
  mappedCreateTelephoneNumber,
  isBusinessUpdated,
} from './utils';

export const fetchCountries: any = createAsyncThunk(
  'profile/fetchCountries',
  async () => {
    try {
      const data = await countriesServices.searchCountries({ isActive: true });
      return getMappedCountries(data);
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const fetchPersonPermissions: any = createAsyncThunk(
  'profile/fetchPersonPermissions',
  async (_: void, thunkAPI) => {
    try {
      const { personsId } = getPerson(thunkAPI.getState() as RootState);

      const businessId: number = getBusinessesId();

      const permissions = await personsServices.getBusinessPermissions(
        personsId,
        businessId,
      );

      return permissions;
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const fetchBusiness: any = createAsyncThunk(
  'profile/fetchBusiness',
  async (businessId: number) => {
    try {
      const business = await getBusinessById(businessId);

      return business;
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const sendInvitation: any = createAsyncThunk(
  'profile/sendInvitation',
  async (message: string, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState;

      const { businessesId } = getBusiness(state);
      const { emailAddress } = getPerson(state);
      const tenant = getTenant(state);

      const inviteData = getUserInviteData({
        businessesId,
        email: emailAddress,
        resendInvite: true,
        message,
        tenant,
      });

      await personsServices.invitePersonsCreate(inviteData);
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const fetchProfileByEmail: any = createAsyncThunk(
  'profile/fetchProfileByEmail',
  async (email: string) => {
    try {
      const person = await personsServices.getPerson(email);

      if (person) {
        const businessId = getBusinessesId();
        const business = await businessesService.getBusinessDetailsById(
          businessId,
        );

        const profileData = getProfileData(person, business);

        return profileData;
      }

      return null;
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const createPersonTelephone: any = createAsyncThunk(
  'profile/createPersonTelephone',
  async (data: any, thunkApi) => {
    try {
      const { emailAddress, personsId } = getPerson(
        thunkApi.getState() as RootState,
      );

      const fields = mappedCreateTelephoneNumber(data, emailAddress);

      const telephoneNumbersId = await telephoneService.addTelephoneNumbers(
        fields,
      );

      let personsTelephoneNumbersXrefid: any = 0;

      if (personsId) {
        const phoneNumberXrefs: any =
          await telephoneService.searchPersonsTelephoneNumbersXrefs({
            personsId: personsId,
            isActive: true,
            telephoneNumbersTypeCode: PERSON_PHONE_TYPE_CODE,
          });

        const phoneNumberXref =
          phoneNumberXrefs && phoneNumberXrefs.length
            ? phoneNumberXrefs[0]
            : null;

        if (phoneNumberXref) {
          personsTelephoneNumbersXrefid =
            await telephoneService.updatePersonsTelephoneNumbersXrefs({
              persons__TelephoneNumbers_XrefId:
                phoneNumberXref.persons__TelephoneNumbers_XrefId,
              telephoneNumbersId,
              isActive: true,
              modifiedBy: data.emailAddress || emailAddress,
            });
        } else {
          personsTelephoneNumbersXrefid =
            await telephoneService.addPersonsTelephoneNumbersXrefs({
              createdBy: data.emailAddress || emailAddress,
              telephoneNumbersTypeCode: PERSON_PHONE_TYPE_CODE,
              personsId: data.personsId || personsId,
              telephoneNumbersId,
              isActive: true,
            });
        }
      } else {
        personsTelephoneNumbersXrefid =
          await telephoneService.addPersonsTelephoneNumbersXrefs({
            createdBy: data.emailAddress || emailAddress,
            telephoneNumbersTypeCode: PERSON_PHONE_TYPE_CODE,
            personsId: data.personsId || personsId,
            telephoneNumbersId,
            isActive: true,
          });
      }

      return {
        telephoneNumber: {
          telephoneNumbersId,
          ...fields,
        },
        telephoneNumberXref: {
          personsTelephoneNumbersXrefid,
        },
      };
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const updatePersonTelephone: any = createAsyncThunk(
  'profile/updatePersonTelephone',
  async (data: any, thunkApi) => {
    try {
      const { person, personTelephoneNumber } = getProfile(
        thunkApi.getState() as RootState,
      );

      const fields = mappedCreateTelephoneNumber(data, person.emailAddress);

      if (
        fields.countryCallingCode !==
          personTelephoneNumber.countryCallingCode ||
        fields.telephoneNumber !== personTelephoneNumber.telephoneNumber
      ) {
        const telephoneNumbersId = await telephoneService.addTelephoneNumbers(
          fields,
        );

        const phoneNumberXrefs: any =
          await telephoneService.searchPersonsTelephoneNumbersXrefs({
            personsId: data.personsId || person.personsId,
            isActive: true,
            telephoneNumbersTypeCode: PERSON_PHONE_TYPE_CODE,
          });

        const phoneNumberXref =
          phoneNumberXrefs && phoneNumberXrefs.length
            ? phoneNumberXrefs[0]
            : null;

        if (phoneNumberXref) {
          await telephoneService.updatePersonsTelephoneNumbersXrefs({
            persons__TelephoneNumbers_XrefId:
              phoneNumberXref.persons__TelephoneNumbers_XrefId,
            telephoneNumbersId,
            isActive: true,
            modifiedBy: data.emailAddress || person.emailAddress,
          });
        } else {
          await telephoneService.addPersonsTelephoneNumbersXrefs({
            createdBy: data.emailAddress || person.emailAddress,
            telephoneNumbersTypeCode: PERSON_PHONE_TYPE_CODE,
            personsId: data.personsId || person.personsId,
            telephoneNumbersId,
            isActive: true,
          });
        }
      }

      return {
        ...personTelephoneNumber,
        ...fields,
      };
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const createBusinessTelephone: any = createAsyncThunk(
  'profile/createBusinessTelephone',
  async (data: any, thunkApi) => {
    try {
      const { business, person, businessTelephoneNumberXref } = getProfile(
        thunkApi.getState() as RootState,
      );

      const fields = mappedCreateTelephoneNumber(data, person.emailAddress);

      const telephoneNumbersId = await telephoneService.addTelephoneNumbers(
        fields,
      );

      let businessesTelephoneNumbersXrefId: any =
        businessTelephoneNumberXref?.businessesTelephoneNumbersXrefId || 0;

      if (businessesTelephoneNumbersXrefId) {
        await telephoneService.updateBusinessesTelephoneNumbersXrefs({
          businessesTelephoneNumbersXrefid: businessesTelephoneNumbersXrefId,
          telephoneNumbersId,
          isActive: true,
          modifiedBy: data.emailAddress || person.emailAddress,
        });
      } else {
        businessesTelephoneNumbersXrefId =
          await telephoneService.addBusinessesTelephoneNumbersXrefs({
            createdBy: data.emailAddress || person.emailAddress,
            telephoneNumbersTypeCode: BUSINESS_PHONE_TYPE_CODE,
            businessesId: data.businessesId || business.businessesId,
            telephoneNumbersId,
            isActive: true,
          });
      }

      return {
        telephoneNumber: {
          telephoneNumbersId,
          ...fields,
        },
        telephoneNumberXref: {
          businessesTelephoneNumbersXrefId,
        },
      };
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const updatePersonInfo: any = createAsyncThunk(
  'profile/updatePersonInfo',
  async (data: PersonalInfoProps, thunkApi) => {
    try {
      const {
        person,
        businessPerson,
        personTelephoneNumber,
        isPreRegistrationPrefill,
      } = getProfile(thunkApi.getState() as RootState);

      const result = { person, businessPerson };

      // 39196 - The pre-registration prefill seeds redux `person` with values that were never persisted, so the
      // form-vs-redux dirty check below would treat an untouched prefill as "no change" and skip the write,
      // leaving the backend's stub name ("NEW PERSON"). Force the write when the data was prefilled; otherwise
      // keep the dirty check so normal onboarding only writes on an actual user change.
      if (
        isPreRegistrationPrefill ||
        person.givenName1 !== data.givenName1 ||
        person.surnameFirst !== data.surnameFirst
      ) {
        const personFields = {
          ...person,
          modifiedBy: person.emailAddress,
          givenName1: data.givenName1,
          surnameFirst: data.surnameFirst,
        };

        await personsServices.updatePersons(personFields);

        result.person = personFields as PersonProps;
      }

      if (
        businessPerson.businessesPersonsXrefid &&
        businessPerson.businessesPersonsRole !== data.businessesPersonsRole
      ) {
        const businessesId = getBusinessesId();

        const businessPersonFields = {
          personsId: businessPerson.personsId,
          businessesId,
          businessesPersonsXrefid: businessPerson.businessesPersonsXrefid,
          modifiedBy: person.emailAddress,
          businessesPersonsRole: data.businessesPersonsRole,
        };

        await businessesService.updateBusinessesPersonsXrefs(
          businessPersonFields,
        );

        result.businessPerson = businessPersonFields as BusinessPersonProps;
      }

      if (data.phone) {
        if (personTelephoneNumber.telephoneNumber) {
          await thunkApi.dispatch(updatePersonTelephone(data));
        } else {
          await thunkApi.dispatch(createPersonTelephone({ phone: data.phone }));
        }
      }

      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const searchCandidatesByAddress: any = createAsyncThunk(
  'profile/searchCandidates',
  async (address: any) => {
    try {
      const result: any = await searchCandidates(address);
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const sendSmartDisburse: any = createAsyncThunk(
  'profile/sendSmartDisburse',
  async (_: void, thunkApi) => {
    try {
      const { person, business } = getProfile(thunkApi.getState() as RootState);

      await addSmartDisburse({
        businessesId: business.businessesId,
        createdBy: person.emailAddress,
      });
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const updateBusiness: any = createAsyncThunk(
  'profile/updateBusiness',
  async (data: any, thunkApi) => {
    try {
      const { person, business } = getProfile(thunkApi.getState() as RootState);

      const fields = {
        ...data,
        businessesId: business.businessesId,
        modifiedBy: person.emailAddress,
      };

      await updateBusinesses(fields);

      if (fields.logoFile || fields.faviconFile) {
        const updatedBusiness = await getBusinessById(business.businessesId);
        return updatedBusiness || fields;
      }

      return fields;
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const fetchOrCreatePerson: any = createAsyncThunk(
  'profile/fetchOrCreatePerson',
  async (user: Partial<UserProps>, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState;

      const tenant = getTenant(state);

      const data: any = await searchOrCreatePerson(user, tenant.tenantsId);

      return {
        ...data,
        checkStep: user.checkStep,
      };
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const createBusinessInfo: any = createAsyncThunk(
  'profile/createBusinessInfo',
  async (data: any, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState;

      const tenant = getTenant(state);
      const profile = getProfile(state);

      const result = await createBusinessProfile({
        data,
        tenant,
        profile,
      });

      // if there is a phone ID and personWasCreated is true
      // it means that business was create for another person
      // and we should create phone record instead of update
      if (
        (profile.personTelephoneNumber?.telephoneNumbersId ||
          data.telephoneNumbersId) &&
        !result.personWasCreated
      ) {
        await thunkApi.dispatch(updatePersonTelephone(data));
      } else {
        await thunkApi.dispatch(
          createPersonTelephone({
            phone: data.phone,
            emailAddress: data.emailAddress,
            personsId: result.person?.personsId || profile.person.personsId,
          }),
        );
      }

      // Copy the same phone to the newly created business so the business
      // has its own phone record from the start.
      await thunkApi.dispatch(
        createBusinessTelephone({
          phone: data.phone,
          emailAddress: data.emailAddress,
          businessesId: result.business?.businessesId,
        }),
      );

      return result;
    } catch (e) {
      if (e instanceof BusinessNotCreatedError) {
        thunkApi.dispatch(
          notify({
            variant: 'error',
            message: e.message,
            translate: e.translate,
            duration: 10000,
          }),
        );
      }

      return Promise.reject(e);
    }
  },
);

export const updateBusinessInfo: any = createAsyncThunk(
  'profile/updateBusinessInfo',
  async (data: any, thunkApi) => {
    try {
      const profile = getProfile(thunkApi.getState() as RootState);

      let result: any = {};

      if (isBusinessUpdated(data, profile)) {
        result = await updateBusinessProfile(data, profile);
      }

      if (
        data.emailAddress !== profile.person?.emailAddress &&
        profile.person?.personsId &&
        !data.isAuthenticated
      ) {
        const personFields = {
          ...profile.person,
          modifiedBy: data.emailAddress,
          emailAddress: data.emailAddress,
        };

        await personsServices.updatePersons(personFields);

        result.person = personFields;
      }

      if (
        profile.personTelephoneNumber?.telephoneNumbersId ||
        data.telephoneNumbersId
      ) {
        if (
          !profile.businessAdmin.emailAddress ||
          profile.businessAdmin.emailAddress === profile.person?.emailAddress
        ) {
          await thunkApi.dispatch(updatePersonTelephone(data));
        }
      } else {
        await thunkApi.dispatch(
          createPersonTelephone({
            phone: data.phone,
            emailAddress: data.emailAddress,
            personsId: profile.person.personsId,
          }),
        );
      }

      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const saveBusiness: any = createAsyncThunk(
  'profile/saveBusiness',
  async (data: any, thunkApi) => {
    try {
      const business = getBusiness(thunkApi.getState() as RootState);

      if (business?.businessesId) {
        await thunkApi.dispatch(updateBusinessInfo(data));
      } else {
        await thunkApi.dispatch(createBusinessInfo(data));
      }
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const authorizeSignatory: any = createAsyncThunk(
  'submitDashboard/authorizeSignatory',
  async (_: void, thunkApi) => {
    try {
      const { person, business } = getProfile(thunkApi.getState() as RootState);

      const businessesId = business.businessesId;

      const businessId = await updateBusinesses({
        businessesId,
        signatoryAttestationPersonsID: person.personsId,
        modifiedBy: person.emailAddress,
      });

      return { businessId, signatoryAttestationPersonsID: person.personsId };
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const addTandCaccept: any = createAsyncThunk(
  'profile/addTandCacceptLogs',
  async (tandCversion: string, thunkApi) => {
    try {
      const { personsId, emailAddress } = getPerson(
        thunkApi.getState() as RootState,
      );

      if (personsId) {
        const businessesId = getBusinessesId();

        const id = await tandcService.addTandCacceptLogs({
          businessesId,
          personsId,
          tandCversion,
          createdBy: emailAddress,
        });

        return id;
      }

      return null;
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const addTandCview: any = createAsyncThunk(
  'profile/addTandCviewLogs',
  async (tandCversion: string, thunkApi) => {
    try {
      const { personsId, emailAddress } = getPerson(
        thunkApi.getState() as RootState,
      );

      if (personsId) {
        const businessesId = getBusinessesId();

        const id = await tandcService.addTandCviewLogs({
          businessesId,
          personsId,
          tandCversion,
          createdBy: emailAddress,
        });

        return id;
      }

      return null;
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

// 39196 - prefill Step 1 from a short-lived token GUID (?key=). Fetches the snapshot from the
// public DMZ endpoint and maps it into the business + address + person slices. Returns the snapshot
// (or null when the key is missing/expired) so the caller can drive verifyPerson on the email.
export const fetchPreRegistrationPrefill: any = createAsyncThunk(
  'profile/fetchPreRegistrationPrefill',
  async (key: string, thunkApi) => {
    try {
      const snapshot = await getPreRegistrationToken(key);

      if (!snapshot) {
        return null;
      }

      // Step C separation: the business email (Step 1) lives on businessAdmin, while person.emailAddress
      // holds the contact/account email (Step 3, and the invite target). Contact email falls back to the
      // business email so the account always has an address when there is no distinct contact email.
      if (snapshot.mainBusinessEmail) {
        thunkApi.dispatch(
          setBusinessAdmin({ emailAddress: snapshot.mainBusinessEmail }),
        );
      }

      const contactEmail = snapshot.contactEmail || snapshot.mainBusinessEmail;
      if (contactEmail) {
        thunkApi.dispatch(setPerson({ emailAddress: contactEmail }));
      }

      thunkApi.dispatch(
        setBusiness({ businessLegalName: snapshot.businessLegalName || '' }),
      );

      // The snapshot carries the country as an alpha-3 code (e.g. "USA"), but the country dropdown
      // matches on the numeric ISO code (e.g. "840"). Resolve it against the countries list so the
      // dropdown preselects; alpha-2 is also needed to parse a national phone number below.
      const businessAddress = getBusinessesAddress(
        thunkApi.getState() as RootState,
      );

      let countryIsocode =
        snapshot.country || businessAddress.countryIsocode || '';
      let alpha2Code = businessAddress.alpha2Code;
      let countryName = businessAddress.country;
      const rawCountry = (snapshot.country || '').trim().toUpperCase();

      if (rawCountry) {
        try {
          const countries = await countriesServices.searchCountries({
            isActive: true,
          });
          const match = countries.find(
            (c) =>
              c.alpha3Code?.toUpperCase() === rawCountry ||
              c.alpha2Code?.toUpperCase() === rawCountry ||
              c.isocode === rawCountry,
          );

          if (match) {
            countryIsocode = match.isocode;
            alpha2Code = match.alpha2Code;
            countryName = match.country1;
          }
        } catch {
          // Leave the country unresolved; the dropdown just won't preselect.
        }
      }

      thunkApi.dispatch(
        setBusinessAddress({
          ...businessAddress,
          addressLine1: snapshot.legalAddress || businessAddress.addressLine1,
          addressLine2: snapshot.addressLine2 || businessAddress.addressLine2,
          city: snapshot.city || businessAddress.city,
          stateOrProvince:
            snapshot.stateProvince || businessAddress.stateOrProvince,
          postalCode: snapshot.postalCode || businessAddress.postalCode,
          countryIsocode,
          alpha2Code,
          country: countryName,
        }),
      );

      // Step C separation: business phone (Step 1) -> businessAdmin; contact phone (Step 3) -> person.
      // Split each to E.164 parts so PhoneInput gets a valid value; unparseable -> leave blank.
      if (snapshot.mainBusinessPhone) {
        try {
          const parsedBusiness = parsePhoneNumber(
            snapshot.mainBusinessPhone,
            alpha2Code as CountryCode,
          );

          if (parsedBusiness) {
            thunkApi.dispatch(
              setBusinessAdmin({
                countryCallingCode: `+${parsedBusiness.countryCallingCode}`,
                telephoneNumber: parsedBusiness.nationalNumber,
              }),
            );
          }
        } catch {
          // Unparseable business phone -> leave it blank for the user to enter.
        }
      }

      if (snapshot.contactPhone) {
        try {
          const parsedContact = parsePhoneNumber(
            snapshot.contactPhone,
            alpha2Code as CountryCode,
          );

          if (parsedContact) {
            thunkApi.dispatch(
              setPersonTelephoneNumber({
                countryCallingCode: `+${parsedContact.countryCallingCode}`,
                telephoneNumber: parsedContact.nationalNumber,
              }),
            );
          }
        } catch {
          // Unparseable contact phone -> leave it blank for the user to enter.
        }
      }

      // 39196 - contact person name for Step 3 ("Tell us how to contact you") from the logged-in user.
      if (snapshot.contactFirstName || snapshot.contactLastName) {
        thunkApi.dispatch(
          setPerson({
            givenName1: snapshot.contactFirstName || '',
            surnameFirst: snapshot.contactLastName || '',
          }),
        );
      }

      return snapshot;
    } catch (e) {
      return Promise.reject(e);
    }
  },
);
